import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import useConsults from 'services/general/consultsList';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import { useShouldShowConsults } from 'hooks/useShouldShowConsultLink';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { getImpersonateId } from 'store/user';
import useFeature from 'services/features/useFeature';
import { Kit, KitStatuses } from '@repo/types';
import { kitResultsViewable, kitStatusParser, parseDate } from '@repo/utils';
import { Icon, Typography, TextColor, StatusPill } from '@repo/ui';
import getKitLabel from '@repo/utils/getKitLabel';

import { TextLink } from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { HomeResultsStatus } from '../HomeNextSteps/HomeNextSteps';
import { FadedCard } from '../FadedCard/FadedCard';

import styles from './KitsSection.module.css';
import KitSectionCardVersion from './KitSectionCardVersion';

const KitSectionCard = ({ kit }: { kit: Kit }) => {
  const { data: consultsList } = useConsults();
  const impersonateId = useSelector(getImpersonateId);
  const { config: portalConfig } = usePortalConfigs(kit);
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );
  const hideSurveyCTAs = portalConfig.survey_type === 'skip';
  const { isFeatureEnabled: isPartnerRecommendationNoteEnabled } = useFeature("partner_recommendation_note")
  const { isFeatureEnabled: resultsHomeFormsCompletion } = useFeature(
    'results_home_forms_completion',
  );
  const isShortSurvey = portalConfig.survey_type === 'short';
  const surveyIncomplete =
    !hideSurveyCTAs && !isShortSurvey && !kit.date_survey_completed;

  const { trackEvent } = useAnalytics();
  const isVoid = kitStatusParser(kit.status) === 'Void';
  const isPractitionerReview =
    kit.status === KitStatuses.RESULTS_READY_PRACTITIONER;
  const resultsViable =
    kitResultsViewable(kit, !!impersonateId) && !!kit.date_survey_completed;
  const kitType = getKitLabel(kit, true);
  const formattedDate = format(
    parseDate(kit.sample_date) ?? new Date(),
    'MM/dd/yy',
  );

  const consultWithNote = useMemo(
    () =>
      consultsList?.find(
        consult =>
          consult.status.includes('completed') &&
          consult.kit_id === kit.id &&
          consult.consult_note?.status === 'published',
      ),
    [consultsList, kit],
  );
  const shouldShowConsultLink = useShouldShowConsults(kit);
  const shouldShowPartnerRecsLink = !shouldShowConsultLink && !consultWithNote?.kit_id && !!kit.partner?.partner_notes?.content && isPartnerRecommendationNoteEnabled;

  const resultsElement =
    resultsHomeFormsCompletion && surveyIncomplete ? (
      <StatusPill status='Blocked - Incomplete forms' color='red' />
    ) : resultsViable ? (
      <TextLink
        label='See Results'
        to={`/results/${kit.id}`}
        state={{ analyticsSource: 'homepage_cta' }}
        size='body-s'
        iconNameRight={
          resultsHomeFormsCompletion ? 'chevronForward' : undefined
        }
      />
    ) : !!kit.date_results_ready ? (
      <HomeResultsStatus text='Ready' />
    ) : (
      (!!kit.date_in_transit_to_lab || !!kit.date_delivered_to_lab) && (
        <HomeResultsStatus text='Lab analysis' />
      )
    );

  const consultOrPartnerElement = !!consultWithNote?.kit_id ? (
    <div className='hide-on-mobile'>
      <TextLink
        label='Specialist Notes'
        to={`/consults/${consultWithNote.kit_id}`}
        size='body-s'
        iconNameRight={
          resultsHomeFormsCompletion ? 'chevronForward' : undefined
        }
      />
    </div>
  ) : shouldShowConsultLink ? (
    <div className='hide-on-mobile'>
      <TextLink
        label={showGroupCalls ? 'Schedule a review' : 'Book a call'}
        to={
          showGroupCalls
            ? `/results/${kit.id}/consults/select-method`
            : `/results/${kit.id}/consults/book`
        }
        size='body-s'
        onClick={() =>
          trackEvent('Schedule Call Clicked', {
            source: 'homepage_kit_card',
          })
        }
      />
    </div>
  ) : shouldShowPartnerRecsLink ? (
    <div className='hide-on-mobile'>
      <TextLink
        label={'Partner Recs'}
        to={`/partner-recs/${kit.partner_id}`}
        size='body-s'
      />
    </div>
  ) : null;

  const surveyCtaElement = !hideSurveyCTAs
    ? !kit.date_survey_completed
      ? (
          <TextLink
            label={
              resultsHomeFormsCompletion && !isShortSurvey
                ? 'Complete health forms'
                : 'Complete info'
            }
            to={`/information/${kit.id}`}
            state={{ from: `/` }}
            size='body-s'
          />
        )
      : resultsHomeFormsCompletion && !kit.date_results_ready
        ? (
            <TextLink
              label='Edit health forms'
              to={`/results/${kit.id}/surveys`}
              state={{ from: `/` }}
              size='body-s'
            />
          )
        : (
            <TextLink
              label={
                resultsHomeFormsCompletion ? 'Health forms' : 'Health responses'
              }
              to={`/results/${kit.id}/surveys`}
              state={{ from: `/` }}
              size='body-s'
              iconNameRight={
                resultsHomeFormsCompletion ? 'chevronForward' : undefined
              }
            />
          )
    : null;

  return (
    <div className={styles.card}>
      <div className='flex align-items-center gap-2 pb-1'>
        <Typography variant='heading-m'>{formattedDate}</Typography>
        <Typography variant='heading-m'>{kitType}</Typography>
      </div>
      <div className='flex align-items-center justify-content-between gap-1 flex-wrap'>
        <div className={styles.iconRow}>
          <Icon name='priceTagFilled' size='s' />
          <Typography variant='body-s'>{`Kit ID: ${kit.id}`}</Typography>
          <KitSectionCardVersion kitId={kit.id} />
        </div>
        <div className={styles.iconRow}>
          <Icon name='atSign' size='s' />
          <Typography variant='body-s'>{kit.sample_label}</Typography>
        </div>
        {isVoid && (
          <div className='flex align-items-center gap-1 red'>
            <Typography variant='body-s'>Void</Typography>
            <Icon name='add' size='s' className={styles.rotate} />
          </div>
        )}
      </div>
      {!isVoid && isPractitionerReview && (
        <div className={styles.practitionerReviewRow}>
          <Typography variant='heading-s' color={TextColor.PURPLE}>
            Practitioner review in progress
          </Typography>
          <Typography variant='body-xs'>
            Your practitioner is reviewing your results and will release them to
            you soon.
          </Typography>
        </div>
      )}
      {!isVoid && (
        <>
          <div className='flex align-items-center justify-content-between gap-2 flex-wrap'>
            {resultsHomeFormsCompletion && resultsViable ? (
              <>
                {surveyCtaElement}
                {consultOrPartnerElement}
                {resultsElement}
              </>
            ) : (
              <>
                {resultsElement}
                {consultOrPartnerElement}
                {surveyCtaElement}
              </>
            )}
          </div>
          {!!consultWithNote?.kit_id && (
            <div className='hide-on-desktop'>
              <TextLink
                label='Specialist Note'
                to={`/consults/${consultWithNote.kit_id}`}
                size='body-s'
                iconNameRight={
                  resultsHomeFormsCompletion ? 'chevronForward' : undefined
                }
              />
            </div>
          )}
          {shouldShowConsultLink && !consultWithNote?.kit_id && (
            <div className='hide-on-desktop'>
              <TextLink
                label={showGroupCalls ? 'Schedule a review' : 'Book a call'}
                to={
                  showGroupCalls
                    ? `/results/${kit.id}/consults/select-method`
                    : `/results/${kit.id}/consults/book`
                }
                size='body-s'
              />
            </div>
          )}
          {shouldShowPartnerRecsLink && (
              <div className='hide-on-desktop'>
                <TextLink
                  label={"Partner Recs"}
                  to={`/partner-recs/${kit.partner_id}`}
                  size='body-s'
                />
              </div>
            )}
        </>
      )}
    </div>
  );
};

export const KitsSection = ({
  tinyAccountId,
}: {
  tinyAccountId: string | null;
}) => {
  const { getDisplayName, kits } = useTinyAccountForHome(tinyAccountId);
  const { trackEvent } = useAnalytics();

  if (!kits?.length) return <></>;
  return (
    <FadedCard>
      <div className={styles.container}>
        <div className='flex align-items-center justify-content-between gap-2'>
          <Typography variant='heading-l'>{`${getDisplayName()} Tests`}</Typography>
          <TextLink
            label='Activate kit'
            iconNameLeft='add'
            iconSize='m'
            to={`/activate`}
            state={{ from: `/` }}
            size={'label'}
            color={TextColor.GREY}
            className='gap-1'
            onClick={() =>
              trackEvent('Activate Kit Clicked', {
                source: 'home_kits_section',
              })
            }
          />
        </div>
        {kits?.map(kit => (
          <KitSectionCard key={kit.id} kit={kit} />
        ))}
      </div>
    </FadedCard>
  );
};
